import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { functionTranslations } from "./functions";
import { readStorage, writeStorage, removeStorage } from "../lib/storage-utils";

const I18N_STORAGE_KEY = "customy.locale";
const LEGACY_STORAGE_KEY = "customy.mockupWorkspace.locale";
const DEFAULT_LOCALE = "en";
const AVAILABLE_LOCALES = ["de", "en"];
const localeLoaders = {
  de: () => import("./locales/de.json"),
  en: () => import("./locales/en.json"),
};
const loadedTranslations = new Map();
const pendingTranslations = new Map();

/* ── Helpers ────────────────────────────────────────────────────────── */

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

function mergeDeep(base, override) {
  if (override === undefined) return base;
  if (!isPlainObject(base) || !isPlainObject(override)) return override;
  const merged = { ...base };
  for (const key of Object.keys(override)) {
    merged[key] = mergeDeep(base[key], override[key]);
  }
  return merged;
}

function normalizeLocale(locale) {
  if (!locale) return DEFAULT_LOCALE;
  const lower = String(locale).toLowerCase();
  if (AVAILABLE_LOCALES.includes(lower)) return lower;
  const base = lower.split("-")[0];
  return AVAILABLE_LOCALES.includes(base) ? base : DEFAULT_LOCALE;
}

function getInitialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  // Migrate legacy key once
  const legacy = readStorage(LEGACY_STORAGE_KEY);
  const stored = readStorage(I18N_STORAGE_KEY);

  if (!stored && legacy) {
    const normalized = normalizeLocale(legacy);
    writeStorage(I18N_STORAGE_KEY, normalized);
    removeStorage(LEGACY_STORAGE_KEY);
    return normalized;
  }

  if (legacy) {
    removeStorage(LEGACY_STORAGE_KEY);
  }

  if (stored && AVAILABLE_LOCALES.includes(stored)) return stored;

  return normalizeLocale(window.navigator?.language);
}

/**
 * Build the full translations object for a locale. The strict i18n audit keeps
 * both JSON catalogs complete, so loading an English base for German would only
 * duplicate startup work.
 */
function buildTranslations(locale, jsonTranslations) {
  return mergeDeep(jsonTranslations, functionTranslations[locale] || {});
}

/** Load and cache one locale chunk. Rejected loads stay retryable. */
export function preloadLocale(locale) {
  const normalized = normalizeLocale(locale);
  const loaded = loadedTranslations.get(normalized);
  if (loaded) return Promise.resolve(loaded);

  const pending = pendingTranslations.get(normalized);
  if (pending) return pending;

  const request = localeLoaders[normalized]()
    .then((module) => {
      const translations = buildTranslations(normalized, module.default);
      loadedTranslations.set(normalized, translations);
      pendingTranslations.delete(normalized);
      return translations;
    })
    .catch((error) => {
      pendingTranslations.delete(normalized);
      throw error;
    });

  pendingTranslations.set(normalized, request);
  return request;
}

/** Preload exactly the persisted/browser locale before the React tree mounts. */
export async function preloadInitialLocale() {
  const locale = getInitialLocale();
  await preloadLocale(locale);
  return locale;
}

/**
 * Resolve a dot-separated key against a nested object.
 * Returns the value or the key itself as fallback.
 */
function resolve(key, obj) {
  const parts = key.split(".");
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return key;
    current = current[part];
  }
  return current === undefined ? key : current;
}

/* ── Context ────────────────────────────────────────────────────────── */

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const initialLocale = useMemo(getInitialLocale, []);
  const [locale, setLocaleState] = useState(initialLocale);
  const [translations, setTranslations] = useState(() => loadedTranslations.get(initialLocale));
  const localeRequest = useRef(0);

  useEffect(() => {
    if (translations) return undefined;

    let active = true;
    preloadLocale(initialLocale)
      .then((loaded) => {
        if (active) setTranslations(loaded);
      })
      .catch((error) => {
        console.error(`Could not load locale "${initialLocale}".`, error);
      });

    return () => {
      active = false;
    };
  }, [initialLocale, translations]);

  useEffect(() => {
    writeStorage(I18N_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(async (next) => {
    const normalized = normalizeLocale(next);
    const requestId = localeRequest.current + 1;
    localeRequest.current = requestId;

    try {
      const loaded = await preloadLocale(normalized);
      if (localeRequest.current !== requestId) return;
      setTranslations(loaded);
      setLocaleState(normalized);
    } catch (error) {
      console.error(`Could not load locale "${normalized}".`, error);
    }
  }, []);

  const t = useCallback(
    (key, params) => {
      const value = resolve(key, translations);
      if (typeof value !== "string") return value; // allow objects & functions
      if (!params) return value;
      return value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
        params[k] !== undefined ? params[k] : `{{${k}}}`,
      );
    },
    [translations],
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      availableLocales: AVAILABLE_LOCALES,
      t,
      translations,
    }),
    [locale, setLocale, t, translations],
  );

  if (!translations) return null;

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access translations.
 *
 *   const { t, locale, setLocale, translations } = useTranslation();
 *   t("header.navigation.dashboard")  // → "Dashboard"
 */
export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useTranslation must be used within <I18nProvider>");
  return ctx;
}

/* ── Convenience hooks (API-compatible with legacy MockupLocale) ──── */

/** Returns the full translations object — consumers access text.dashboard.title etc. */
export const useAppText = () => useTranslation().translations;

export const useHeaderText = () => useTranslation().translations.header;

export const useWorkspaceText = () => useTranslation().translations.workspace;

export const useGenerationText = () => useTranslation().translations.generation;

export const useGenerationConfig = () => useTranslation().translations.config;
